import React from 'react';

/**
 * Describes how each prop should be extracted from a streaming JSON string.
 *
 * - "wait-for-complete": the prop is only forwarded once its value is fully received in the JSON
 *   stream (e.g. a string whose closing `"` has arrived, or a number/boolean that is followed by
 *   a delimiter). Use this for props where a partial value would cause an error (e.g. `chartType`).
 *
 * - "stream-characters": the partial string value is forwarded character-by-character as it
 *   arrives. Use this for display strings like `chartTitle` where showing "A" before "Apple" is
 *   fine.
 *
 * - "stream-complete-items": the prop is an array and each element is only forwarded once it is
 *   fully received (i.e. its closing `}` or `]` or `"` has arrived). Partial array elements are
 *   withheld. Use this for `data` arrays.
 */
export type PropStreamingMode = 'wait-for-complete' | 'stream-characters' | 'stream-complete-items';

type KeysOfUnion<T> = T extends T ? keyof T : never;

export type WidgetPropSchema<TProps extends object> = {
	[K in KeysOfUnion<TProps>]?: PropStreamingMode;
};

type WidgetComponent<TProps extends object> =
	| React.ComponentType<TProps>
	| React.LazyExoticComponent<React.ComponentType<TProps>>;

export type WidgetWrapperProps<TProps extends object> = {
	/** The streaming JSON string (may be incomplete / partially received). */
	jsonStream: string;
	/** The wrapped component to render. */
	component: WidgetComponent<TProps>;
	/** Streaming behaviour for each prop.*/
	propSchema: WidgetPropSchema<TProps>;
	/** Extra props merged into the component's props after streaming extraction. */
	extraProps?: Partial<TProps>;
};

/**
 * Extracts the raw text of a JSON value that begins at `startIndex` inside `json`.
 * Returns `null` when the value hasn't started yet.
 * Returns `{ raw, complete }` where `complete` is true when the closing delimiter has arrived.
 *
 * Works for strings, numbers, booleans, null, objects and arrays.
 */
function extractJsonValue(
	json: string,
	startIndex: number,
): { raw: string; complete: boolean } | null {
	if (startIndex >= json.length) {
		return null;
	}

	const ch = json[startIndex];

	if (ch === '"') {
		// String value — scan for the unescaped closing quote.
		let i = startIndex + 1;
		while (i < json.length) {
			if (json[i] === '\\') {
				i += 2; // skip escaped character
				continue;
			}
			if (json[i] === '"') {
				return { raw: json.slice(startIndex, i + 1), complete: true };
			}
			i++;
		}
		// Closing quote not yet received — partial string.
		return { raw: json.slice(startIndex), complete: false };
	}

	if (ch === '[' || ch === '{') {
		// Object or array — track nesting depth.
		let depth = 0;
		let inString = false;
		let i = startIndex;
		while (i < json.length) {
			const c = json[i];
			if (inString) {
				if (c === '\\') {
					i += 2;
					continue;
				}
				if (c === '"') {
					inString = false;
				}
			} else {
				if (c === '"') {
					inString = true;
				} else if (c === '[' || c === '{') {
					depth++;
				} else if (c === ']' || c === '}') {
					depth--;
					if (depth === 0) {
						return { raw: json.slice(startIndex, i + 1), complete: true };
					}
				}
			}
			i++;
		}
		return { raw: json.slice(startIndex), complete: false };
	}

	// Number, boolean, null — only complete once followed by a structural
	// delimiter (optionally after whitespace). End-of-input alone is not enough
	// for streaming because a trailing `42` may still become `421` in the next
	// chunk.
	const match = json.slice(startIndex).match(/^(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null)/);
	if (match) {
		const end = startIndex + match[0].length;
		let i = end;
		while (i < json.length && (json[i] === ' ' || json[i] === '\t' || json[i] === '\n')) {
			i++;
		}
		const after = json[i];
		const complete = after === ',' || after === '}' || after === ']';
		return { raw: match[0], complete };
	}

	return null;
}

/**
 * Finds the index in `json` where the value for `key` starts.
 * Returns -1 if the key hasn't appeared yet.
 */
function findKeyValueStart(json: string, key: string): number {
	const keyPattern = `"${key}"`;
	let searchFrom = 0;
	while (searchFrom < json.length) {
		const keyIdx = json.indexOf(keyPattern, searchFrom);
		if (keyIdx === -1) {
			return -1;
		}
		// Skip past key + optional whitespace + colon + optional whitespace.
		let i = keyIdx + keyPattern.length;
		while (i < json.length && (json[i] === ' ' || json[i] === '\t' || json[i] === '\n')) {
			i++;
		}
		if (json[i] === ':') {
			i++;
			while (i < json.length && (json[i] === ' ' || json[i] === '\t' || json[i] === '\n')) {
				i++;
			}
			return i;
		}
		searchFrom = keyIdx + 1;
	}
	return -1;
}

/**
 * Extracts fully-received elements from a streaming JSON array.
 * E.g. `[{"x":1},{"x":2}` → returns `[{"x":1},{"x":2}]` if both are complete.
 * An unclosed trailing element is dropped.
 */
function extractCompleteArrayItems(arrayRaw: string): unknown[] | null {
	// arrayRaw may be complete or partial (missing closing `]`).
	if (!arrayRaw.startsWith('[')) {
		return null;
	}
	const items: unknown[] = [];
	let i = 1; // skip opening `[`
	while (i < arrayRaw.length) {
		// Skip whitespace and commas between elements.
		while (
			i < arrayRaw.length &&
			(arrayRaw[i] === ',' || arrayRaw[i] === ' ' || arrayRaw[i] === '\n' || arrayRaw[i] === '\t')
		) {
			i++;
		}
		if (i >= arrayRaw.length || arrayRaw[i] === ']') {
			break;
		}
		const result = extractJsonValue(arrayRaw, i);
		if (!result) {
			break;
		}
		if (!result.complete) {
			// Element not yet complete — stop here.
			break;
		}
		try {
			items.push(JSON.parse(result.raw));
		} catch {
			break;
		}
		i += result.raw.length;
	}
	return items;
}

/**
 * Decodes JSON string escape sequences from a possibly-partial string body.
 *
 * This intentionally tolerates incomplete trailing escapes so streamed text can
 * continue rendering as characters arrive.
 */
function decodePartialJsonStringContent(value: string): string {
	let result = '';

	for (let i = 0; i < value.length; i += 1) {
		const char = value[i];

		if (char !== '\\') {
			result += char;
			continue;
		}

		if (i === value.length - 1) {
			result += '\\';
			break;
		}

		const escapedChar = value[i + 1];

		switch (escapedChar) {
			case '"':
				result += '"';
				i += 1;
				break;
			case '\\':
				result += '\\';
				i += 1;
				break;
			case '/':
				result += '/';
				i += 1;
				break;
			case 'b':
				result += '\b';
				i += 1;
				break;
			case 'f':
				result += '\f';
				i += 1;
				break;
			case 'n':
				result += '\n';
				i += 1;
				break;
			case 'r':
				result += '\r';
				i += 1;
				break;
			case 't':
				result += '\t';
				i += 1;
				break;
			case 'u': {
				const hex = value.slice(i + 2, i + 6);
				if (/^[0-9a-fA-F]{4}$/.test(hex)) {
					result += String.fromCharCode(parseInt(hex, 16));
					i += 5;
					break;
				}
				result += '\\';
				break;
			}
			default:
				result += '\\';
				break;
		}
	}

	return result;
}

/**
 * Parses a partial (streaming) JSON object string and extracts props according to the schema.
 *
 * - "wait-for-complete": only emits the prop once its JSON value is fully received.
 * - "stream-characters": emits a partial string value as characters arrive (excluding quotes).
 * - "stream-complete-items": emits array elements as they are fully received.
 *
 * Props not listed in the schema default to "wait-for-complete".
 */
function extractStreamingProps<TProps extends object>(
	jsonStream: string,
	propSchema: WidgetPropSchema<TProps>,
	knownKeys: KeysOfUnion<TProps>[],
): Partial<TProps> {
	const result: Partial<TProps> = {};
	const mutableResult = result as Partial<Record<keyof TProps, unknown>>;

	for (const key of knownKeys) {
		const mode: PropStreamingMode = propSchema[key] ?? 'wait-for-complete';
		const valueStart = findKeyValueStart(jsonStream, String(key));
		if (valueStart === -1) {
			continue;
		}

		const extracted = extractJsonValue(jsonStream, valueStart);
		if (!extracted) {
			continue;
		}

		if (mode === 'wait-for-complete') {
			if (extracted.complete) {
				try {
					mutableResult[key] = JSON.parse(extracted.raw);
				} catch {
					// ignore malformed
				}
			}
		} else if (mode === 'stream-characters') {
			// For strings: strip the surrounding quotes and pass the partial content.
			const raw = extracted.raw;
			if (raw.startsWith('"')) {
				// If complete, strip both quotes; if partial, strip only the opening quote.
				const inner = extracted.complete ? raw.slice(1, -1) : raw.slice(1);
				mutableResult[key] = decodePartialJsonStringContent(inner);
			} else if (extracted.complete) {
				try {
					mutableResult[key] = JSON.parse(raw);
				} catch {
					// ignore
				}
			}
		} else if (mode === 'stream-complete-items') {
			const items = extractCompleteArrayItems(extracted.raw);
			if (items !== null && items.length > 0) {
				mutableResult[key] = items;
			}
		}
	}

	return result;
}

/**
 * WidgetWrapper is a generic component that processes a streaming JSON string and feeds
 * extracted props into a wrapped component according to a per-prop streaming schema.
 *
 * @example
 * ```tsx
 * <WidgetWrapper
 *   jsonStream={chartConfigJson}
 *   component={VisualizationChart}
 *   propSchema={{
 *     chartType: 'wait-for-complete',
 *     chartTitle: 'stream-characters',
 *     data: 'stream-complete-items',
 *   }}
 *   extraProps={{ testId: localId, messageId, cloudId: siteId }}
 * />
 * ```
 */
export function WidgetWrapper<TProps extends object>({
	jsonStream,
	component: Component,
	propSchema,
	extraProps,
}: WidgetWrapperProps<TProps>): JSX.Element | null {
	const knownKeys = React.useMemo(
		() => Object.keys(propSchema) as KeysOfUnion<TProps>[],
		[propSchema],
	);
	const streamedProps = extractStreamingProps(jsonStream, propSchema, knownKeys);
	const mergedProps = { ...streamedProps, ...extraProps } as TProps;
	return React.createElement(Component as React.ComponentType<TProps>, mergedProps);
}
